"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="mb-4 rounded-full bg-[var(--color-green-accent)]/20 p-3">
          <svg className="h-8 w-8 text-[var(--color-green-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mb-2 font-serif text-2xl text-foreground">Thank You</h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-full border-[var(--color-gold-accent)] text-foreground hover:bg-[var(--color-gold-accent)] hover:text-white">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="bg-background/50 border-border/50 focus-visible:border-[var(--color-green-accent)] focus-visible:ring-[var(--color-green-accent)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background/50 border-border/50 focus-visible:border-[var(--color-green-accent)] focus-visible:ring-[var(--color-green-accent)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background/50 border-border/50 focus-visible:border-[var(--color-green-accent)] focus-visible:ring-[var(--color-green-accent)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">How can we help you?</FormLabel>
            <FormControl>
              <Textarea placeholder="I'm interested in learning more about..." className="min-h-[120px] resize-none bg-background/50 border-border/50 focus-visible:border-[var(--color-green-accent)] focus-visible:ring-[var(--color-green-accent)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-md bg-[#8FA88F] text-primary-foreground transition-colors duration-300 hover:bg-[#8FA88F]/90 hover:text-primary-foreground text-xs font-medium tracking-[0.2em] uppercase mt-2">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending...
          </> : "Submit Request"}
      </Button>
    </Form>;
}
export default function HeroContact() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="herocontact" className="relative overflow-hidden bg-background py-32">
      {}
      <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[var(--color-gold-accent)]/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[var(--color-green-accent)]/5 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          
          {}
          <div ref={leftRef} className={`flex flex-col justify-center transition-all duration-1000 ease-out ${isLeftVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <h1 className="mb-10 text-center font-sans text-4xl font-medium leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
              Begin Your Protocol
            </h1>

            <div className="relative rounded-3xl border border-[var(--color-gold-accent)]/40 bg-white/60 p-6 shadow-xl shadow-black/5 backdrop-blur-md sm:p-10 dark:bg-black/40">
              <FormProvider formId="hero-contact-form" sectionName="Hero Contact" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} submitText="Submit Request" successMessage="Thank you for reaching out. A member of our team will contact you shortly to begin your protocol.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative h-full min-h-[500px] w-full overflow-hidden rounded-3xl transition-all duration-1000 delay-200 ease-out lg:min-h-[700px] ${isRightVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/serene_clinical_environment_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/serene_clinical_environment_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/serene_clinical_environment_hero.png-wvc-sizes" alt="Serene clinical wellness environment at MO Protocol" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent mix-blend-overlay" />
          </div>

        </div>
      </div>
    </section>;
}